import {
  Box,
  Typography,
  Container,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  Divider,
  Link,
} from "@mui/material";
import Navbar from "./Navbar";
import Footer from "./Footer";
import Form from "./Form";
import {
  FaBook,
  FaInfinity,
  FaRegCreditCard,
  FaUserGraduate,
  FaEnvelope,
  FaPhoneAlt,
  FaMapMarkerAlt,
} from "react-icons/fa";
import { motion } from "framer-motion";

const features = [
  { icon: <FaBook />, title: "Extensive Resources" },
  { icon: <FaInfinity />, title: "Lifetime Privileges" },
  { icon: <FaRegCreditCard />, title: "Affordable Education" },
  { icon: <FaUserGraduate />, title: "Dedicated Support" },
];

export default function ContactUs() {
  return (
    <>
      <Navbar />

      {/* ✅ Hero Section */}
      <Box
        sx={{
          background: '#8a88bc',
          color: "white",
          textAlign: "center",
          py: { xs: 6, md: 10 },
        }}
      >
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          Thank you for your interest in Medby Academy
        </Typography>
        <Typography variant="h6" maxWidth="700px" mx="auto">
          We’re here to assist you with any questions, concerns, or feedback about our online medical courses,  certificate programs, CPD, and other services.
        </Typography>
      </Box>


      {/* ✅ Contact Form */}
      {<Form />}

      {/* ✅ Contact Details Section */}
      <Box
        sx={{
          backgroundColor: "white",
          py: { xs: 8, md: 10 },
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
        }}
      >
        <Container maxWidth="lg">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <Paper
              elevation={6}
              sx={{
                p: { xs: 4, md: 6 },
                borderRadius: 5,
                backdropFilter: "blur(12px)",
                background: "rgba(255, 255, 255, 0.15)",
                color: "black",
                boxShadow: "0 8px 30px rgba(0,0,0,0.2)",
              }}
            >
              {/* Heading */}
              <Typography
                variant="h4"
                fontWeight="bold"
                align="center"
                sx={{ mb: 5, letterSpacing: 1, color: "black" }}
              >
                Get in Touch with <span style={{ color: "#00ffc3" }}>Medby Academy</span>
              </Typography>

              {/* Contact Info */}
              <Grid container spacing={5}>
                {[
                  {
                    icon: <FaEnvelope />,
                    title: "Email",
                    info: "support@medbyacademy.com",
                    desc: "For inquiries, course questions, or support — expect a reply in 2–3 days.",
                  },
                  {
                    icon: <FaPhoneAlt />,
                    title: "Phone",
                    info: "+91-8193060696",
                    desc: "Available Monday–Saturday, 10:00 AM – 6:00 PM IST.",
                  },
                  {
                    icon: <FaMapMarkerAlt />,
                    title: "Address",
                    info: "Sector 63, Noida, India",
                    desc: "Office not open for walk-ins; please contact us first.",
                  },
                ].map((item, index) => (
                  <Grid item xs={12} md={4} key={index}>
                    <motion.div
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    >
                      <Box
                        sx={{
                          textAlign: "center",
                          p: 3,
                          borderRadius: 3,
                          background: "rgba(255, 255, 255, 0.1)",
                          transition: "0.3s",
                        }}
                      >
                        <Box
                          sx={{
                            fontSize: 36,
                            color: "#00ffc3",
                            mb: 1.5,
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Typography variant="h6" fontWeight="bold">
                          {item.title}
                        </Typography>
                        <Typography variant="body1" sx={{ mt: 0.5 }}>
                          {item.info}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ mt: 1.5, color: "rgba(0, 0, 0, 0.8)" }}
                        >
                          {item.desc}
                        </Typography>
                      </Box>
                    </motion.div>
                  </Grid>
                ))}
              </Grid>

              <Divider sx={{ my: 5, borderColor: "rgba(255,255,255,0.3)" }} />

              {/* Support Section */}
              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Support for Specific Needs
                </Typography>
                <List>
                  {[
                    "Course Enrollment & Payments: Email support@medbyacademy.com for enrollment support.",
                    "Technical Issues: Send details (screenshots if possible) to info@medbyacademy.com.",
                    "Feedback & Suggestions: Share your thoughts via email to help us improve.",
                  ].map((text, i) => (
                    <ListItem key={i} sx={{ pl: 0 }}>
                      <ListItemText primary={text} />
                    </ListItem>
                  ))}
                </List>
              </motion.div>

              <Divider sx={{ my: 5, borderColor: "rgba(255,255,255,0.3)" }} />

              {/* Social Links */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <Typography variant="h5" fontWeight="bold" gutterBottom>
                  Connect With Us
                </Typography>
                <Typography variant="body1" gutterBottom>
                  Stay updated on new courses, promotions, and medical insights:
                </Typography>

                <Box sx={{ display: "flex", gap: 4, flexWrap: "wrap", mt: 1 }}>
                  {[
                    { name: "Facebook", url: "https://www.facebook.com/share/17FigCiyJT/" },
                    { name: "Instagram", url: "https://www.instagram.com/medby.academy?igsh=MTBrZ2k0Y3ltaGN2aw==" },
                    { name: "YouTube", url: "https://youtube.com/@medbyacademy?si=WqCB7P-Bo6aT1y9g" },
                  ].map((link, idx) => (
                    <motion.div key={idx} whileHover={{ scale: 1.1 }}>
                      <Link
                        href={link.url}
                        target="_blank"
                        underline="hover"
                        sx={{
                          fontWeight: "bold",
                          color: "#00ffc3",
                          fontSize: "1.1rem",
                          letterSpacing: 0.5,
                        }}
                      >
                        {link.name}
                      </Link>
                    </motion.div>
                  ))}
                </Box>
              </motion.div>

              <Divider sx={{ my: 5, borderColor: "rgba(255,255,255,0.3)" }} />

              {/* Response Time */}
              <Typography variant="h5" fontWeight="bold" gutterBottom>
                Response Time
              </Typography>
              <Typography variant="body1">
                We aim to respond to all inquiries within <strong>2–3 business days</strong>.
                For urgent matters, please call us during business hours or mark your email as
                high priority.
              </Typography>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {<Footer />}
    </>
  );
}
